import { useState } from "react";

import { useProposalDocument } from "../proposalDocumentsContext";

interface UseCollapsibleArgs {
  // Anchor id for this item — must match the id used on its DOM root and
  // in the outline rail's nav entries
  id: string;
  hasError: boolean;
  // The parent group this item belongs to (e.g. a SupplierQuoteCard passes
  // its section's anchor id) — lets a targeted rail navigation collapse
  // this item's siblings within the same group without touching unrelated
  // sections/cards. Sections themselves have no group, so they're never
  // force-collapsed by a sibling-collapse request.
  groupId?: string;
}

// Shared collapse/expand behaviour for QuoteSection and SupplierQuoteCard:
// starts expanded, auto-expands when this item's own validation error
// appears, and responds to expand-one / bulk expand-all-collapse-all
// commands broadcast via context from the outline rail and quotes.tsx.
// Each check below keys off a distinct dependency so these sources never
// fight each other - a bulk command only fires on an explicit button click
// (new token), never as a side effect of validation, and vice versa.
export function useCollapsible({ id, hasError, groupId }: UseCollapsibleArgs) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const { quotesExpandRequest, quotesBulkCommand } = useProposalDocument();

  const [prevHasError, setPrevHasError] = useState(hasError);
  const [prevExpandRequest, setPrevExpandRequest] = useState(quotesExpandRequest);
  const [prevBulkCommand, setPrevBulkCommand] = useState(quotesBulkCommand);

  if (hasError !== prevHasError) {
    setPrevHasError(hasError);
    if (hasError) setIsCollapsed(false);
  }

  if (quotesExpandRequest !== prevExpandRequest) {
    setPrevExpandRequest(quotesExpandRequest);
    if (quotesExpandRequest) {
      if (quotesExpandRequest.ids.includes(id)) {
        setIsCollapsed(false);
      } else if (
        quotesExpandRequest.collapseSiblingsInGroup !== undefined &&
        groupId === quotesExpandRequest.collapseSiblingsInGroup
      ) {
        setIsCollapsed(true);
      }
    }
  }

  if (quotesBulkCommand !== prevBulkCommand) {
    setPrevBulkCommand(quotesBulkCommand);
    if (quotesBulkCommand) setIsCollapsed(quotesBulkCommand.action === "collapse");
  }

  return {
    isCollapsed,
    toggle: () => {
      setIsCollapsed((c) => !c);
    },
  };
}
